<template>
  <BDLayout>
    <div class="transaction">
      <section id="bd-client-detail">
        <div class="container">
          <div class="d-flex justify-content-between align-items-center mb-3">
            <div>
              <h6 class="fw-bold mb-1">{{ client?.name || 'Client Detail' }}</h6>
              <div class="text-muted small">
                <span v-if="client">{{ client.country }}</span>
                <span v-if="client && client.sector"> · {{ client.sector.name }}</span>
              </div>
            </div>
          </div>

          <div class="row g-3 mb-3" v-if="client">
            <div class="col-md-3 col-sm-6" v-for="card in summaryCards" :key="card.label">
              <div class="card shadow-sm border-0 h-100">
                <div class="card-body py-3">
                  <div class="text-muted small">{{ card.label }}</div>
                  <div class="h4 mb-0">{{ card.value }}</div>
                </div>
              </div>
            </div>
          </div>

          <ul class="nav nav-tabs mb-3">
            <li class="nav-item">
              <button
                class="nav-link"
                :class="{ active: currentTab === 'opportunities' }"
                @click="currentTab = 'opportunities'"
              >
                Opportunities ({{ opportunities.length }})
              </button>
            </li>
            <li class="nav-item">
              <button
                class="nav-link"
                :class="{ active: currentTab === 'contracts' }"
                @click="currentTab = 'contracts'"
              >
                Contracts ({{ contracts.length }})
              </button>
            </li>
            <li class="nav-item">
              <button
                class="nav-link"
                :class="{ active: currentTab === 'losses' }"
                @click="currentTab = 'losses'"
              >
                Losses ({{ losses.length }})
              </button>
            </li>
          </ul>

          <div v-if="loading" class="text-center py-3">
            <span class="spinner-border spinner-border-sm me-2"></span>
            Loading client details...
          </div>

          <div v-else>
            <div v-if="currentTab === 'opportunities'">
              <table id="clientDetailTable" ref="clientDetailTable" class="table table-sm table-hover mb-0">
                <thead>
                  <tr>
                    <th>Ref</th>
                    <th>Title</th>
                    <th>Status</th>
                    <th>Department</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="o in opportunities" :key="o.id">
                    <td>{{ o.tenderReference }}</td>
                    <td>{{ o.tenderTitle }}</td>
                    <td>{{ o.status }}</td>
                    <td>{{ o.departmentId }}</td>
                  </tr>
                  <tr v-if="!opportunities.length">
                    <td colspan="4" class="text-muted">No opportunities found.</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div v-else-if="currentTab === 'contracts'">
              <table id="clientDetailTable" ref="clientDetailTable" class="table table-sm table-hover mb-0">
                <thead>
                  <tr>
                    <th>Award Date</th>
                    <th>Value</th>
                    <th>Status</th>
                    <th>Opportunity Ref</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="c in contracts" :key="c.id">
                    <td>{{ c.awardDate?.substring(0, 10) }}</td>
                    <td>{{ c.contractValue }}</td>
                    <td>{{ c.status }}</td>
                    <td>{{ c.opportunity?.tenderReference }}</td>
                  </tr>
                  <tr v-if="!contracts.length">
                    <td colspan="4" class="text-muted">No contracts found.</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div v-else>
              <table id="clientDetailTable" ref="clientDetailTable" class="table table-sm table-hover mb-0">
                <thead>
                  <tr>
                    <th>Reason</th>
                    <th>Competitor</th>
                    <th>Lessons Learned</th>
                    <th>Opportunity Ref</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="l in losses" :key="l.id">
                    <td>{{ l.lossReason }}</td>
                    <td>{{ l.competitor }}</td>
                    <td>{{ l.lessonsLearned }}</td>
                    <td>{{ l.submission?.opportunity?.tenderReference }}</td>
                  </tr>
                  <tr v-if="!losses.length">
                    <td colspan="4" class="text-muted">No losses found.</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>
    </div>
  </BDLayout>
</template>

<script>
import { onMounted, ref, computed, nextTick, watch } from 'vue'
import { useRoute } from 'vue-router'
import BDLayout from '@/components/BDLayout.vue'
import $ from 'jquery'
import 'datatables.net-vue3'
import 'datatables.net-bs5'
import 'datatables.net-buttons-bs5'
import 'datatables.net-responsive-bs5'
import 'jszip'
import 'datatables.net-buttons/js/buttons.html5.js'
import 'datatables.net-buttons/js/buttons.colVis.js'
import 'datatables.net-buttons/js/buttons.print.js'

export default {
  name: 'BidClientDetail',
  components: { BDLayout },
  setup() {
    const route = useRoute()
    const id = route.params.id

    const client = ref(null)
    const opportunities = ref([])
    const contracts = ref([])
    const losses = ref([])
    const loading = ref(false)
    const currentTab = ref('opportunities')
    const clientDetailTable = ref(null)
    let dataTable = null

    const initializeDataTable = () => {
      try {
        if (!clientDetailTable.value) return

        if (dataTable) {
          dataTable.destroy()
          dataTable = null
        }

        dataTable = $(clientDetailTable.value).DataTable({
          dom: 'Bfrtip',
          responsive: true,
          language: {
            paginate: {
              previous: '<i class="fas fa-chevron-left"></i>',
              next: '<i class="fas fa-chevron-right"></i>',
            },
          },
          buttons: [
            'colvis',
            'pageLength',
            {
              extend: 'csv',
              text: 'Export CSV',
              className: 'btn btn-primary btn-sm',
              exportOptions: {
                columns: ':visible',
              },
            },
            {
              extend: 'excel',
              text: 'Export Excel',
              className: 'btn btn-primary btn-sm',
              exportOptions: {
                columns: ':visible',
              },
            },
            {
              extend: 'print',
              text: 'Print',
              className: 'btn btn-secondary btn-sm',
              exportOptions: {
                columns: ':visible',
              },
            },
          ],
        })
      } catch (error) {
        console.error('Failed to initialize client detail DataTable', error)
      }
    }

    const loadData = async () => {
      loading.value = true
      try {
        const base = (process.env.VUE_APP_URL || 'http://localhost:3000').replace(/\/+$/, '')
        const headers = {
          Accept: 'application/json',
          ...(localStorage.getItem('access_token')
            ? { Authorization: `Bearer ${JSON.parse(localStorage.getItem('access_token'))}` }
            : {}),
        }

        const [clientRes, oppsRes, contractsRes, lossesRes] = await Promise.all([
          fetch(`${base}/clients/${id}`, { headers }),
          fetch(`${base}/clients/${id}/opportunities`, { headers }),
          fetch(`${base}/clients/${id}/contracts`, { headers }),
          fetch(`${base}/clients/${id}/losses`, { headers }),
        ])

        const clientJson = await clientRes.json().catch(() => ({}))
        const oppsJson = await oppsRes.json().catch(() => ({}))
        const contractsJson = await contractsRes.json().catch(() => ({}))
        const lossesJson = await lossesRes.json().catch(() => ({}))

        client.value = clientJson.data || clientJson || null
        opportunities.value = Array.isArray(oppsJson.data || oppsJson) ? oppsJson.data || oppsJson : []
        contracts.value = Array.isArray(contractsJson.data || contractsJson)
          ? contractsJson.data || contractsJson
          : []
        losses.value = Array.isArray(lossesJson.data || lossesJson)
          ? lossesJson.data || lossesJson
          : []

        await nextTick()
        initializeDataTable()
      } catch (err) {
        console.error('Failed to load client detail', err)
      } finally {
        loading.value = false
      }
    }

    const summaryCards = computed(() => {
      if (!client.value) return []
      return [
        { label: 'Opportunities', value: opportunities.value.length },
        { label: 'Contracts', value: contracts.value.length },
        { label: 'Losses', value: losses.value.length },
      ]
    })

    onMounted(loadData)

    watch(currentTab, async () => {
      if (loading.value) return
      await nextTick()
      initializeDataTable()
    })

    return {
      client,
      opportunities,
      contracts,
      losses,
      loading,
      currentTab,
      summaryCards,
      clientDetailTable,
    }
  },
}
</script>
